import { setShown } from './util/dom';
import { RouteData, Coordinate } from './util/route-data';
import { get as dbGet } from './util/db';

// Install the handlebar helpers.
import * as Handlebars from 'handlebars';
import './handlebar-helpers';

import { computeMileMarker } from './util/gps';
import { registerServiceWorker } from './util/service-worker-client';
import { CURRENT_MILE_UNKNOWN, State } from './app/state';
import { ViewManager } from './app/view_manager';
import { assert } from './util/assert';
import { ParsedGeoLocation, requestGeoLocation, setInitialCachedLocation } from './util/geo-location';
import { historyManager } from './util/history_state';

let initialized = false;
let state: State|null = null;

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;

const MAX_SAVED_LOCATION_AGE = 2 * HOURS;

async function initialize() {
  await registerServiceWorker();

  const kmzData = await dbGet();
  if (!kmzData) {
    window.location.href = '/import-route.html';
    return;
  }

  let requestingPreInitPosition = true;
  let preInitPosition: ParsedGeoLocation|null = null;
  let locationUpdateError = false;

  // If we have a location saved in the history state, use it as its
  // less than MAX_SAVED_LOCATION_AGE.
  if (history.state?.location) {
    const location = history.state.location;
    const timePassed = Date.now() - location.lastUpdated;
    if (timePassed > 0 && timePassed < MAX_SAVED_LOCATION_AGE) {
      preInitPosition = history.state.location;
      setInitialCachedLocation(history.state.location);
    }
  }

  requestGeoLocation(
    (loc: ParsedGeoLocation) => {
      requestingPreInitPosition = false;
      if (!initialized) {
        preInitPosition = loc;
      } else {
        updateStateWithNewLocation(loc);
      }
    },
    () => {
      if (!initialized) {
        requestingPreInitPosition = false;
        locationUpdateError = true;
      } else {
        state!.update({ locationUpdating: false, locationUpdateError: true });
      }
    });

  // Process the data.
  // TODO - this is gross, modifying the raw data that comes back from the DB.
  for (const spot of kmzData.spots) {
    if (spot.description) {
      spot.description = new Handlebars.SafeString(spot.description as string);
    }
  }

  state = createState(kmzData, preInitPosition, requestingPreInitPosition, locationUpdateError);
  setShown('loading', false);
  new ViewManager(state, startLocationUpdate);

  setInterval(startLocationUpdate, 30000);

  initialized = true;
}

function startLocationUpdate() {
  assert(state);
  state.update({locationUpdating: true, locationUpdateError: false});
  requestGeoLocation(
    (loc: ParsedGeoLocation) => {
      updateStateWithNewLocation(loc);
    },
    () => {
      state!.update({locationUpdating: false, locationUpdateError: true});
    });
}

function createState(kmzData: RouteData,
                     preInitPosition: ParsedGeoLocation|null,
                     requestingPreInitPosition: boolean,
                     locationUpdateError: boolean,): State {
  const currentMile = calculateMileFromPos(preInitPosition, kmzData);
  const locationLastUpdated = preInitPosition ? preInitPosition.lastUpdated : 0;
  const locationAccuracy = preInitPosition ? preInitPosition.accuracy : '';
  return new State(kmzData, currentMile, requestingPreInitPosition,
      locationUpdateError, locationLastUpdated, locationAccuracy);
}

function updateStateWithNewLocation(loc: ParsedGeoLocation) {
  const currentMile = calculateMileFromPos(loc, state!.kmz);
  state!.update({
    currentMile,
    locationUpdating: false,
    locationUpdateError: false,
    locationAccuracy: loc.accuracy,
    locationLastUpdated: loc.lastUpdated,
  });
  historyManager.updateState();
}

function calculateMileFromPos(loc: ParsedGeoLocation|null, kmz: RouteData): number {
  if (!loc) {
     return CURRENT_MILE_UNKNOWN;
  }
  return computeMileMarker(kmz.route, loc.coord);
}

initialize();
